import {
  fetchStrapiCollection,
  getStrapiMediaUrl,
  hasStrapiConfig,
} from "./client";
import { tribunalsInfo as fallbackTribunalDetails } from "../../components/resources/data/data";
import { menuItems as fallbackMenuItems } from "../../components/dropdowns/menuItems";

export function slugifyTribunalName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const slugAliases = {
  "business-premises-tribunal": "business-tribunal",
  "business-premises-rent-tribunal": "business-tribunal",
  "communications-and-multi-media-appeals-tribunal-camat":
    "communication-and-multimedia-appeals-tribunal",
  "communication-and-multi-media-appeals-tribunal":
    "communication-and-multimedia-appeals-tribunal",
  "land-acqusition-tribunal": "land-acquisition-tribunal",
  "retirement-benefits-appeals-tribunal": "retirements-benefits-appeals-tribunal",
};

function sortByOrder(items) {
  return [...items].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
}

function ensurePath(pathname) {
  if (!pathname) {
    return "";
  }

  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

function normalizeStringList(items, key) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      if (typeof item === "string") {
        return item.trim();
      }

      if (item && typeof item === "object") {
        return String(item[key] || item.name || item.title || "").trim();
      }

      return "";
    })
    .filter(Boolean);
}

function normalizeLinkList(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const label = String(item.label || item.title || item.name || "").trim();
      const url = String(item.url || item.link || "").trim();

      if (!url) {
        return null;
      }

      return {
        label: label || url,
        url,
      };
    })
    .filter(Boolean);
}

function mapStrapiTribunal(item) {
  return {
    id: item.id,
    slug: item.slug || slugifyTribunalName(item.name),
    tribunalName: item.name || "",
    image: getStrapiMediaUrl(item.heroImage),
    description: item.description || item.summary || item.mandate || "",
    chairman: item.chairperson || "",
    membersNo: item.membersNo || item.panelSize || "",
    members: normalizeStringList(item.members, "memberName"),
    jurisdiction: normalizeStringList(item.jurisdiction, "jurisdictionName"),
    qrcode: getStrapiMediaUrl(item.qrCode),
    location: item.location || item.address || "",
    email: item.email || "",
    virtualCourtLink: item.virtualCourtLink || "",
    virtualCourtLinks: normalizeLinkList(item.virtualCourtLinks),
    filingUrl: item.filingUrl || "https://efiling.court.go.ke/",
    judgmentsUrl: item.judgmentsUrl || "",
  };
}

function mapFallbackTribunal(item) {
  const rawName = item.tribunalNmae || item.tribunalName || item.name || "";

  return {
    id: item.id,
    slug: slugifyTribunalName(rawName),
    tribunalName: rawName,
    image: item.image || "",
    description: item.description || "",
    chairman: item.chairman || "",
    membersNo: item.membersNo || "",
    members: normalizeStringList(item.members, "memberName"),
    jurisdiction: normalizeStringList(item.jurisdiction, "jurisdictionName"),
    qrcode: item.qrcode || "",
    location: item.location || "",
    email: item.email || "",
    virtualCourtLink: item.virtualCourtLink || item.virtualcourtlink || "",
    virtualCourtLinks: Array.isArray(item.virtualCourtLinks) ? item.virtualCourtLinks : [],
    filingUrl: "https://efiling.court.go.ke/",
    judgmentsUrl: "",
  };
}

function findFallbackTribunal(slug) {
  const normalizedSlug = slugAliases[slug] || slug;

  return fallbackTribunalDetails
    .map(mapFallbackTribunal)
    .find((item) => item.slug === normalizedSlug);
}

export async function getTribunalDetail(slug) {
  const fallbackTribunal = findFallbackTribunal(slug);

  if (!hasStrapiConfig()) {
    return fallbackTribunal || null;
  }

  try {
    const matches = await fetchStrapiCollection(
      `/api/tribunals?filters[slug][$eq]=${encodeURIComponent(
        slug
      )}&populate[heroImage]=true&populate[qrCode]=true&populate[members]=true&populate[jurisdiction]=true&populate[virtualCourtLinks]=true`
    );

    if (matches.length > 0) {
      return mapStrapiTribunal(matches[0]);
    }
  } catch (error) {
    console.error("Falling back to local tribunal detail because Strapi is unavailable.", error);
  }

  return fallbackTribunal || null;
}

function mapTribunalNavLeaf(item) {
  const slug = item.slug || slugifyTribunalName(item.name || item.title || "");
  const path = ensurePath(item.path) || `/tribunals/${slug}`;

  return {
    title: item.name || item.title || "",
    path,
  };
}

function mapStrapiCategory(category) {
  const tribunals = Array.isArray(category.tribunals?.data)
    ? category.tribunals.data
    : Array.isArray(category.tribunals)
      ? category.tribunals
      : [];

  return {
    title: category.name || "",
    sortOrder: category.sortOrder || 0,
    submenu: sortByOrder(tribunals).map(mapTribunalNavLeaf),
  };
}

function mapFallbackCategoryLeaf(item) {
  return {
    title: item.title || "",
    path: ensurePath(item.path),
  };
}

export const fallbackTribunalNavigation = (
  fallbackMenuItems.find((item) => item.title === "Tribunals")?.submenu || []
).map((category) => ({
  title: category.title,
  submenu: (category.submenu || []).map(mapFallbackCategoryLeaf),
}));

export async function getTribunalNavigation() {
  if (!hasStrapiConfig()) {
    return fallbackTribunalNavigation;
  }

  try {
    const categories = await fetchStrapiCollection(
      "/api/tribunal-categories?populate[tribunals]=true"
    );

    if (categories.length > 0) {
      return sortByOrder(categories).map(mapStrapiCategory).filter((item) => item.submenu.length > 0);
    }
  } catch (error) {
    console.error("Falling back to local tribunal navigation because Strapi is unavailable.", error);
  }

  return fallbackTribunalNavigation;
}
